import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG Imports
import { SharedModule } from 'src/app/private/shared/shared.module';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from 'primeng/tabview';
import { PanelModule } from 'primeng/panel';
import { DragDropModule } from 'primeng/dragdrop';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DividerModule } from 'primeng/divider';

// Services & Models
import { FormBuilderService } from 'src/app/private/services/form-builder.service';
import { 
  DynamicForm, 
  Field, 
  FormFieldConfig, 
  Module,
  AVAILABLE_MODULES,
  UI_TYPE_MAPPINGS 
} from './models/form-builder.model';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CreateFormComponent } from './create-form/create-form.component';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputTextarea,
    DropdownModule,
    CheckboxModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TabViewModule,
    PanelModule,
    DragDropModule,
    CardModule,
    ChipModule,
    TooltipModule,
    InputSwitchModule,
    DividerModule,
    SharedModule,
    CreateFormComponent
  ],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class FormBuilderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  forms: DynamicForm[] = [];
  availableModules = AVAILABLE_MODULES;
  uiTypeMappings = UI_TYPE_MAPPINGS;
  
  // Loading States
  loading = false;
  loadingFields = false;
  savingForm = false;
  publishingForm = false;
  deletingForm = false;
  
  // Dialog States
  showFormDialog = false;
  showFieldConfigDialog = false;
  showEmbedCodeDialog = false;
  isEditMode = false;
  publishConfirmation: any = null;
  
  // Embed Code
  embedCodeForm: DynamicForm | null = null;
  embedCode: string = '';
  
  // Current Form Being Edited
  currentForm: DynamicForm = this.getEmptyForm();
  
  // Available Fields (Left Panel)
  availableFields: Field[] = [];
  
  // Selected Field for Configuration (Right Panel)
  selectedFieldConfig: FormFieldConfig | null = null;
  
  // Drag & Drop
  draggedField: Field | null = null;
  
  // Active Tab
  activeTab = 0;

  constructor(
    private formBuilderService: FormBuilderService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.loadForms();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ Data Loading ============
  
  loadForms(): void {
    this.loading = true;
    let organization_id = this.localStorage.getOrganizationId();
    let campus_id = this.localStorage.getCampusId();
    this.formBuilderService.getDynamicFormList(organization_id, campus_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Transform API response to component format
          this.forms = (response || []).map((form: any) => {
            // If form_field_config exists, map it to fields
            if (form.form_field_config && !form.fields) {
              form.fields = form.form_field_config;
            }
            
            // If module_id is missing but module_name exists, find and set module_id
            if (!form.module_id && form.module_name) {
              const moduleMatch = this.availableModules.find(m => m.name === form.module_name);
              if (moduleMatch) {
                form.module_id = moduleMatch.id;
              }
            }
            
            return form;
          });
          this.loading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load forms'
          });
          this.loading = false;
        }
      });
  }

  loadFieldsByModule(moduleKey: string): void {
    this.loadingFields = true;

    const payload = {
      organization_id: this.localStorage.getOrganizationId(),
      module_type : moduleKey
    };

    this.formBuilderService.getFieldsByModule(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          let dynamicFields = response.fields || [];
          let fixedFields = this.fixedColumnsMap[moduleKey] || [];
          // Mark fields with their source type
          const markedFixedFields = fixedFields.map((f : any)=> ({ ...f, _field_source: 'static' }));
          const markedDynamicFields = dynamicFields.map((f : any)=> ({ ...f, _field_source: 'dynamic' }));
          this.availableFields = [...markedFixedFields, ...markedDynamicFields];
          console.log("Fields loaded:", this.availableFields);
          this.loadingFields = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load fields'
          });
          this.loadingFields = false;
        }
      });
  }

  // ============ Form Management ============
  
  openNewFormDialog(): void {
    // Reset all state FIRST before opening
    this.resetFormState();
    this.isEditMode = false;
    // Create a completely fresh empty form with a new object reference
    this.currentForm = {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getOrganizationId()
    };
    this.availableFields = [];
    this.selectedFieldConfig = null;
    this.showFormDialog = true;
    this.activeTab = 0;
  }

  closeFormDialog(): void {
    this.showFormDialog = false;
    // Reset state after dialog closes
    setTimeout(() => {
      this.resetFormState();
    }, 300); // Wait for dialog close animation
  }

  resetFormState(): void {
    this.currentForm = {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getOrganizationId()
    };
    this.availableFields = [];
    this.selectedFieldConfig = null;
    this.isEditMode = false;
    this.savingForm = false;
    this.loadingFields = false;
    this.activeTab = 0;
  }

  editForm(form: DynamicForm): void {
    this.isEditMode = true;
    
    // Deep clone and transform API structure to component structure
    this.currentForm = JSON.parse(JSON.stringify(form));
    
    // Transform form_field_config to fields if it exists
    if ((this.currentForm as any).form_field_config) {
      this.currentForm.fields = (this.currentForm as any).form_field_config;
      delete (this.currentForm as any).form_field_config;
    }
    
    // Ensure fields array exists
    if (!this.currentForm.fields) {
      this.currentForm.fields = [];
    }
    
    // Load fields for the form's module
    // Try to find module by ID first, then by name (for API response compatibility)
    let module = this.availableModules.find(m => m.id === form.module_id);
    if (!module && this.currentForm.module_name) {
      module = this.availableModules.find(m => m.name === this.currentForm.module_name);
      // Set the module_id if found
      if (module) {
        this.currentForm.module_id = module.id;
      }
    }
    
    if (module) {
      this.loadFieldsByModule(module.key);
    } else {
      console.warn('Module not found for form:', this.currentForm.module_name || this.currentForm.module_id);
      this.availableFields = [];
    }
    
    this.showFormDialog = true;
    this.activeTab = 1; // Go to form builder tab
  }

  deleteForm(form: DynamicForm): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the form "${form.form_name}"?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (form.id) {
          this.deletingForm = true;
          const payload = {
            id: form.id
          };
          this.formBuilderService.deleteForm(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Form deleted successfully'
                });
                this.deletingForm = false;
                this.confirmationService.close();
                this.loadForms();
              },
              error: (error) => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: error.error?.message || 'Failed to delete form'
                });
                this.deletingForm = false;
              }
            });
        }
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  saveForm(): void {
    // The form data is already updated via two-way binding from create-form component
    // Client-side validation
    const validationResult = this.formBuilderService.validateFormConfig(this.currentForm);
    
    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: error
        });
      });
      return;
    }

    this.savingForm = true;
    
    // For new forms, save as draft. For existing forms, preserve the published state
    if (!this.isEditMode) {
      this.currentForm.is_published = false;
    }
    // If editing, keep the current is_published state as is
    
    this.currentForm.organization_id = this.localStorage.getOrganizationId();
    this.currentForm.campus_id = this.localStorage.getCampusId();
    
    const request = this.isEditMode && this.currentForm.id
      ? this.formBuilderService.updateForm(this.currentForm)
      : this.formBuilderService.createForm(this.currentForm);

    request.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.isEditMode 
              ? `Form updated successfully. Status: ${this.currentForm.is_published ? 'Published' : 'Draft'}`
              : 'Form created successfully as draft'
          });
          this.savingForm = false;
          this.closeFormDialog();
          this.loadForms();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to save form'
          });
          this.savingForm = false;
        }
      });
  }

  publishForm(form: DynamicForm): void {
    if (!form.id) return;

    this.publishConfirmation = {
      form: form,
      isPublished: true
    };

    this.confirmationService.confirm({
      message: 'Publishing will make this form publicly accessible. Are you sure you want to continue?',
      header: 'Publish Form',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Publish',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.updateFormStatus(form, true);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  unpublishForm(form: DynamicForm): void {
    if (!form.id) return;

    this.publishConfirmation = {
      form: form,
      isPublished: false
    };

    this.confirmationService.confirm({
      message: 'Unpublishing will make this form no longer publicly accessible. Are you sure you want to continue?',
      header: 'Unpublish Form',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Unpublish',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.updateFormStatus(form, false);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  updateFormStatus(form: DynamicForm, isPublished: boolean): void {
    if (!form.id) return;

    this.publishingForm = true;

    // Create payload with updated status
    const payload = {
      ...form,
      is_published: isPublished

    };

    this.formBuilderService.updateForm(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Form ${isPublished ? 'published' : 'unpublished'} successfully`
          });
          this.publishingForm = false;
          this.publishConfirmation = null;
          this.confirmationService.close();
          this.loadForms();
        },
        error: (error) => {
          const errorMessage = error.error?.message || (isPublished ? 'Failed to publish form' : 'Failed to unpublish form');
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
          this.publishingForm = false;
        }
      });
  }

  // ============ Module Selection ============
  
  onModuleChange(): void {
    const module = this.availableModules.find(m => m.name === this.currentForm.module_name);
    console.log("Module changed:", module);
    if (module) {
      this.currentForm.module_name = module.name;
      this.currentForm.fields = []; // Clear fields when module changes
      this.loadFieldsByModule(module.key);
      this.activeTab = 1; // Move to form builder tab
    }
  }

  // ============ Drag & Drop ============
  
  onFieldDragStart(field: Field): void {
    this.draggedField = field;
  }

  onFieldDragEnd(): void {
    this.draggedField = null;
  }

  onFieldDrop(): void {
    if (this.draggedField) {
      // Check if field already exists in form
      const exists = this.currentForm.fields.some(
        f => f.field_id === this.draggedField!.id
      );

      if (exists) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Field already added to form'
        });
      } else {
        // Create new field config
        const newFieldConfig: FormFieldConfig = {
          field_id: this.draggedField.id,
          field: this.draggedField,
          ui_type: this.getDefaultUIType(this.draggedField.data_type),
          is_required: this.draggedField.is_required || false,
          placeholder: '',
          help_text: '',
          order: this.currentForm.fields.length,
          options: this.draggedField.options || []
        };

        this.currentForm.fields.push(newFieldConfig);
      }

      this.draggedField = null;
    }
  }

  // ============ Field Configuration ============
  
  configureField(fieldConfig: FormFieldConfig): void {
    this.selectedFieldConfig = JSON.parse(JSON.stringify(fieldConfig)); // Deep clone
    this.showFieldConfigDialog = true;
  }

  saveFieldConfiguration(): void {
    if (this.selectedFieldConfig) {
      const index = this.currentForm.fields.findIndex(
        f => f.field_id === this.selectedFieldConfig!.field_id
      );
      
      if (index !== -1) {
        this.currentForm.fields[index] = this.selectedFieldConfig;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Field configuration saved'
        });
      }
    }
    
    this.showFieldConfigDialog = false;
    this.selectedFieldConfig = null;
  }

  removeField(fieldConfig: FormFieldConfig): void {
    this.currentForm.fields = this.currentForm.fields.filter(
      f => f.field_id !== fieldConfig.field_id
    );
    
    // Update order
    this.currentForm.fields.forEach((f, index) => {
      f.order = index;
    });
  }

  moveFieldUp(index: number): void {
    if (index > 0) {
      const temp = this.currentForm.fields[index];
      this.currentForm.fields[index] = this.currentForm.fields[index - 1];
      this.currentForm.fields[index - 1] = temp;
      
      // Update order
      this.currentForm.fields.forEach((f, idx) => {
        f.order = idx;
      });
    }
  }

  moveFieldDown(index: number): void {
    if (index < this.currentForm.fields.length - 1) {
      const temp = this.currentForm.fields[index];
      this.currentForm.fields[index] = this.currentForm.fields[index + 1];
      this.currentForm.fields[index + 1] = temp;
      
      // Update order
      this.currentForm.fields.forEach((f, idx) => {
        f.order = idx;
      });
    }
  }

  // ============ UI Type Management ============
  
  getCompatibleUITypes(dataType: string): any[] {
    return this.uiTypeMappings.filter(
      ui => ui.compatibleDataTypes.includes(dataType)
    );
  }

  getDefaultUIType(dataType: string): string {
    const mapping: { [key: string]: string } = {
      'string': 'text',
      'text': 'textarea',
      'number': 'number',
      'boolean': 'checkbox',
      'date': 'date',
      'enum': 'select'
    };
    return mapping[dataType] || 'text';
  }

  onUITypeChange(): void {
    // Reset options if UI type doesn't support them
    if (this.selectedFieldConfig && 
        !['select', 'radio', 'multiselect'].includes(this.selectedFieldConfig.ui_type)) {
      this.selectedFieldConfig.options = [];
    }
  }

  // ============ Public URL ============
  
  copyPublicUrl(form: DynamicForm): void {
    if (form.public_token) {
      const url = this.formBuilderService.getPublicFormUrl(form.public_token);
      navigator.clipboard.writeText(url).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Public URL copied to clipboard'
        });
      });
    }
  }

  // ============ Embed Code ============
  
  openEmbedCodeDialog(form: DynamicForm): void {
    if (form.public_token) {
      this.embedCodeForm = form;
      this.embedCode = this.generateEmbedCode(form);
      this.showEmbedCodeDialog = true;
    }
  }

  closeEmbedCodeDialog(): void {
    this.showEmbedCodeDialog = false;
    this.embedCodeForm = null;
    this.embedCode = '';
  }

  generateEmbedCode(form: DynamicForm): string {
    const publicUrl = this.formBuilderService.getPublicFormUrl(form.public_token!);
    return `<iframe src="${publicUrl}" 
        width="100%" 
        height="600" 
        frameborder="0" 
        scrolling="auto"
        style="border: 1px solid #ddd; border-radius: 8px;">
</iframe>`;
  }

  copyEmbedCode(): void {
    navigator.clipboard.writeText(this.embedCode).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Embed code copied to clipboard'
      });
    });
  }

  // ============ Helpers ============
  
  getEmptyForm(): DynamicForm {
    return {
      form_name: '',
      module_id: '',
      description: '',
      is_published: false,
      fields: [],
      organization_id: this.localStorage.getOrganizationId()
    };
  }

  getModuleName(moduleId: string): string {
    const module = this.availableModules.find(m => m.id === moduleId);
    return module ? module.name : 'Unknown';
  }

  showOptions(fieldConfig: FormFieldConfig): boolean {
    return ['select', 'radio', 'multiselect'].includes(fieldConfig.ui_type);
  }


  fixedColumnsMap: any = {
    lead: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'query_type', label: 'Query Type' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'interested_course', label: 'Interested Course' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'source', label: 'Source' },
      { field: 'lead_status', label: 'Lead Status' }
    ],
    registration: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'status', label: 'Status' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'preferred_course', label: 'Preferred Course' },
      { field: 'document_status', label: 'Document Status' },
      { field: 'source', label: 'Source' }
    ],
    application: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'preferred_location', label: 'Preferred Location' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'preferred_course', label: 'Preferred Course' },
      { field: 'source', label: 'Source' },
      { field: 'application_status', label: 'Application Status' },
      { field: 'document_status', label: 'Document Status' }
    ],
    enrollment: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'contact_phone', label: 'Contact Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'address', label: 'Address' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' }
    ],
    admission: [
      { field: 'prefix', label: 'Prefix' },
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'email', label: 'Contact Email' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'alternate_phone', label: 'Alternate Number' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
      { field: 'blood_group', label: 'Blood Group' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'country', label: 'Country' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_contact', label: 'Guardian Contact' },
      { field: 'previous_education', label: 'Previous Education' },
      { field: 'address', label: 'Address' }
    ],
    student: [
      { field: 'admission_number', label: 'Admission Number' },
      { field: 'student_roll_no', label: 'Student Roll No' },
      { field: 'admission_id', label: 'Admission ID' },
      { field: 'academic_year', label: 'Academic Year' },
      { field: 'admission_type', label: 'Admission Type' },
      { field: 'admission_mode', label: 'Admission Mode' },
      { field: 'first_name', label: 'First Name' },
      { field: 'middle_name', label: 'Middle Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'gender', label: 'Gender' },
      { field: 'dob', label: 'Date of Birth' },
      { field: 'age', label: 'Age' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'religion', label: 'Religion' },
      { field: 'caste', label: 'Caste' },
      { field: 'caste_category', label: 'Caste Category' },
      { field: 'aadhar_no', label: 'Aadhar Number' },
      { field: 'passport_no', label: 'Passport Number' },
      { field: 'email', label: 'Email' },
      { field: 'alternate_email', label: 'Alternate Email' },
      { field: 'phone', label: 'Phone' },
      { field: 'alternate_phone', label: 'Alternate Phone' },
      { field: 'address_line1', label: 'Address Line 1' },
      { field: 'address_line2', label: 'Address Line 2' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'postal_code', label: 'Postal Code' },
      { field: 'country', label: 'Country' },
      { field: 'permanent_address', label: 'Permanent Address' },
      { field: 'current_address', label: 'Current Address' },
      { field: 'guardian_name', label: 'Guardian Name' },
      { field: 'guardian_relation', label: 'Guardian Relation' },
      { field: 'guardian_phone', label: 'Guardian Phone' },
      { field: 'guardian_email', label: 'Guardian Email' },
      { field: 'guardian_address', label: 'Guardian Address' },
      { field: 'father_name', label: 'Father Name' },
      { field: 'father_phone', label: 'Father Phone' },
      { field: 'father_email', label: 'Father Email' },
      { field: 'father_occupation', label: 'Father Occupation' },
      { field: 'mother_name', label: 'Mother Name' },
      { field: 'mother_phone', label: 'Mother Phone' },
      { field: 'mother_email', label: 'Mother Email' },
      { field: 'mother_occupation', label: 'Mother Occupation' },
      { field: 'annual_income', label: 'Annual Income' },
      { field: 'previous_school', label: 'Previous School' },
      { field: 'previous_class', label: 'Previous Class' },
      { field: 'previous_percentage', label: 'Previous Percentage' },
      { field: 'transfer_certificate', label: 'Transfer Certificate' },
      { field: 'achievements', label: 'Achievements' },
      { field: 'course_id', label: 'Course' },
      { field: 'department_id', label: 'Department' }
    ]
  };
}
